import React, { useEffect, useRef, useState } from 'react';
import { Box, Copy, H6, Icon, Link, Optional } from '@honda-canada/design-system-react';
import { InView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import Error from '../PriceComponent/Error';
import Loader from '../../utils/components/Loader';
import ProtectionCard from './shared/ProtectionCard';
import SubCategoriesDropdown from './shared/SubCategoriesDropdown';
import ViewMoreCard from './shared/ViewMoreCard';
import gtmViewAllOptionsClick from '../../core/gtm/gtmViewAllOptionsClick';
import themeStyles from './AccessoriesCardPanel.styles';
import useProtectionData from './hooks/useProtectionData';
import { IN_VIEW_ROOT_MARGIN, IN_VIEW_STYLE, IN_VIEW_THRESHOLD, PROTECTION } from './utils/constants';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles, selectModelNameByModelKey, } from '../../core/reducers/vehicles';
import { setCurrentViewMoreModalId } from '../../core/reducers/ui';
import { useAppDispatch, useAppSelector } from '../../core/utils';
const HeaderContainer = themeStyles.apply(Box, 'HeaderContainer');
const AccessoryCardContainer = themeStyles.apply(Box, 'AccessoryCardContainer');
const ProtectionCardsContainer = themeStyles.apply(Box, 'ProtectionCardsContainer');
const ViewMoreAccessoriesCta = themeStyles.apply(Link, 'ViewMoreAccessoriesCta');
const ProtectionPanel = ({ categoryId, handleInViewChange, protectionOptions, protections, selectedProtectionSubCategory, defaultProtectionSubCategory, setProtectionSelectedSubCategory, componentName, isFeaturedSubCategorySelected, }) => {
    const { isDataLoaded, isDataFailed, isPaymentFailed } = useProtectionData();
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const modelName = useAppSelector(selectModelNameByModelKey);
    const [showLoader, setShowLoader] = useState(true);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isMounted = useRef(false);
    const MAX_CARDS_DISPLAYED = 8;
    const handleViewMoreClick = () => {
        dispatch(setCurrentViewMoreModalId(categoryId));
        setProtectionSelectedSubCategory(t('Pages.Bap.allProtectionsLabel'), true);
        gtmViewAllOptionsClick({
            model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
            trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
            gtmBodyStyle,
            sectionName: PROTECTION,
        });
    };
    useEffect(() => {
        isMounted.current = true;
    }, []);
    useEffect(() => {
        if (isDataLoaded || isDataFailed) {
            setShowLoader(false);
        }
    }, [isDataLoaded, isDataFailed]);
    const renderItems = () => {
        if (!protections)
            return null;
        const items = protections
            .slice(0, MAX_CARDS_DISPLAYED)
            .map(({ key, title, titleEn, ...props }) => (React.createElement(ProtectionCard, { key: key, id: key, name: title, gtmName: titleEn, componentName: componentName, ...props })));
        if (protections.length > MAX_CARDS_DISPLAYED) {
            items.push(React.createElement(ViewMoreCard, { key: "view-more-card", handleViewMoreClick: handleViewMoreClick, isHorizontal: true, headingTitle: t('Pages.Bap.additionalProtectionLabel') }));
        }
        return items;
    };
    const showErrorMessage = isDataFailed || isPaymentFailed;
    const showProtectionCards = !isPaymentFailed && !isDataFailed && (protections === null || protections === void 0 ? void 0 : protections.length);
    const isSectionHidden = isDataLoaded && !(protections === null || protections === void 0 ? void 0 : protections.length);
    if (isSectionHidden)
        return null;
    return (React.createElement(InView, { as: "div", onChange: inView => handleInViewChange(inView, categoryId), id: `${categoryId}-section`, rootMargin: IN_VIEW_ROOT_MARGIN, style: IN_VIEW_STYLE, threshold: IN_VIEW_THRESHOLD },
        React.createElement(AccessoryCardContainer, { "data-testid": "cy-accessory-card-panel-protection" },
            React.createElement(HeaderContainer, null,
                React.createElement(H6, { id: `${categoryId}-title`, as: "h3", "data-testid": `cy-config-${t('Pages.Bap.protectionLabel')}` }, t('Pages.Bap.protectionLabel')),
                React.createElement(Icon, { name: "plusLogo" })),
            React.createElement(Optional, { when: showLoader },
                React.createElement(Loader, { testId: "cy-loading-state" })),
            React.createElement(Optional, { when: showErrorMessage },
                React.createElement(Error, { message: t('Pages.Bap.apiLoadingError'), verticalAlignment: "center", errorIconName: "exclamationAlert" })),
            React.createElement(Optional, { when: showProtectionCards },
                React.createElement(ProtectionCardsContainer, null,
                    React.createElement(ViewMoreAccessoriesCta, { styling: "tertiary2", "data-testid": "view-more-protections-cta", width: "max-content", onClick: handleViewMoreClick },
                        t('Pages.Bap.viewMoreProtectionLabel'),
                        React.createElement(Icon, { name: "arrowRight", ml: "xs", iconColor: "blue" })),
                    React.createElement(Optional, { when: isFeaturedSubCategorySelected },
                        React.createElement(Copy, { size: "regular", mt: "m", "data-testid": "cy-model-name-protection" }, t('Pages.Bap.protectionBlurbText', { modelName }))),
                    React.createElement(SubCategoriesDropdown, { selectedValue: selectedProtectionSubCategory, defaultValue: defaultProtectionSubCategory, setSelectedSubCategory: setProtectionSelectedSubCategory, options: protectionOptions, className: "protectionSubCategoriesDropdown" }),
                    React.createElement(Box, { mt: "m" }, renderItems()))))));
};
export default ProtectionPanel;
